import { Link, type To } from "react-router";
import type { CustomerSession } from "@shared-core/data/customer-api-types";
import { usePortalViewer } from "./PortalViewerContext";
import { POLLARDS_PHONE } from "./portalContact";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { AlertTriangle } from "lucide-react";

// The three states a CIS order's or quote's page has before it has a page to show (PortalCisOrderPage,
// PortalQuoteDetailPage). Not found is one answer for an unknown number, a malformed one and one the person may
// not see (the server's bare 404), and says so in the words the Tharstern order page uses: for own work, that it
// may be a colleague's; for the whole customer, that it is not theirs at all.

export function CisRecordLoading({ what }: { what: "order" | "quote" }) {
  return (
    <Card
      aria-busy="true"
      className="p-8 text-center text-sm text-muted-foreground"
    >
      Loading {what}…
    </Card>
  );
}

export function CisRecordError({
  what,
  message,
  onRetry,
}: {
  what: "order" | "quote";
  message: string;
  onRetry: () => void;
}) {
  return (
    <Card className="items-center gap-3 p-8 text-center">
      <AlertTriangle className="size-6 text-muted-foreground" />
      <p className="font-medium">We couldn&apos;t load this {what}</p>
      <p className="text-sm text-muted-foreground">{message}</p>
      <Button variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </Card>
  );
}

const WORDING = {
  order: {
    plural: "Orders",
    ownWorkExcludes: "Orders placed by colleagues",
    ownNot: "isn't one of yours",
    customerNot: "orders",
  },
  quote: {
    plural: "Quotes",
    ownWorkExcludes: "Quotes sent to colleagues",
    ownNot: "wasn't sent to you",
    customerNot: "quotes",
  },
} as const;

export function CisRecordNotFound({
  what,
  backTo,
}: {
  what: "order" | "quote";
  backTo: To;
}) {
  const { sessionState } = usePortalViewer();
  const session: CustomerSession | null =
    sessionState.status === "success" ? sessionState.data : null;
  const customerName = session?.customer_name ?? "your company";
  const wording = WORDING[what];
  return (
    <Card className="items-center gap-3 p-8 text-center">
      <p className="font-medium">We can&apos;t show you this {what}</p>
      {session?.visibility === "own_work" ? (
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          It either doesn&apos;t exist, or it {wording.ownNot}.{" "}
          {wording.ownWorkExcludes} aren&apos;t included. To see all of{" "}
          {customerName}&apos;s work, ask your Pollards contact to switch it on
          for you, or call{" "}
          <span className="whitespace-nowrap">{POLLARDS_PHONE}</span>.
        </p>
      ) : (
        <p className="mx-auto max-w-md text-sm text-muted-foreground">
          It either doesn&apos;t exist, or it isn&apos;t one of {customerName}
          &apos;s {wording.customerNot}. If you think it should be, call{" "}
          <span className="whitespace-nowrap">{POLLARDS_PHONE}</span>.
        </p>
      )}
      <div>
        <Button asChild variant="outline">
          <Link to={backTo}>Back to {wording.plural.toLowerCase()}</Link>
        </Button>
      </div>
    </Card>
  );
}
